import React, { useEffect, useState } from 'react';
import { CreditCard, Download, Loader2, AlertCircle, CheckCircle, Clock, Banknote, Shield, Check, Info, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { PRICING_PLANS } from '../../utils/constants';
import { convertPrice, EXCHANGE_RATE } from '../../utils/helpers';

export default function Billing() {
  const { profile, user, updateProfile } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const rawPlanName = profile?.subscription_plan || 'Free';
  const planName = (!rawPlanName || rawPlanName === 'none' || rawPlanName === 'placement_fee') ? 'Free' : rawPlanName;
  const planStatus = profile?.subscription_status || 'inactive';

  const [currency, setCurrency] = useState('NGN');
  const [selectedPlanId, setSelectedPlanId] = useState('basic');
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'transfer'
  const [paymentType, setPaymentType] = useState(profile?.subscription_payment_type || 'full'); // 'full' or 'installment'
  const [isUploading, setIsUploading] = useState(false);
  const [checkoutType, setCheckoutType] = useState('subscription'); // 'subscription' or 'remaining' or 'placement'
  const [triggerPayment, setTriggerPayment] = useState(false);
  const [notification, setNotification] = useState({ type: '', text: '' });
  const [showRemainingModal, setShowRemainingModal] = useState(false);
  const [remainingMethod, setRemainingMethod] = useState('card'); // 'card' or 'transfer'
  const [placementMethod, setPlacementMethod] = useState('card'); // 'card' or 'transfer'
  const [customAmount, setCustomAmount] = useState('');
  const [showRenewalSection, setShowRenewalSection] = useState(false);

  // Phone check states
  const [phoneRequiredModal, setPhoneRequiredModal] = useState({ isOpen: false, callback: null });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const ensurePhoneAndProceed = (actionCallback) => {
    if (!profile?.phone) {
      setPhoneError('');
      setPhoneNumber('');
      setPhoneRequiredModal({
        isOpen: true,
        callback: actionCallback
      });
    } else {
      actionCallback();
    }
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setPhoneError('Please enter a valid phone number.');
      return;
    }
    
    const cleaned = phoneNumber.trim();
    
    // Support any foreign or local format: digits, space, plus, dash, parenthesis, length 7 to 20
    // Just check it has at least 7 digits
    const digitsOnly = cleaned.replace(/\D/g, '');
    if (digitsOnly.length < 7) {
      setPhoneError('Phone number must have at least 7 digits.');
      return;
    }

    setSavingPhone(true);
    setPhoneError('');
    try {
      const { error } = await updateProfile({ phone: cleaned });
      if (error) throw error;
      
      const nextAction = phoneRequiredModal.callback;
      setPhoneRequiredModal({ isOpen: false, callback: null });
      
      if (nextAction) {
        nextAction();
      }
    } catch (err) {
      console.error('Error updating phone number:', err);
      setPhoneError(err.message || 'Failed to save phone number. Please try again.');
    } finally {
      setSavingPhone(false);
    }
  };

  const showNotification = (type, text, autoDismiss = true) => {
    setNotification({ type, text });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (autoDismiss) {
      setTimeout(() => {
        setNotification(prev => prev.text === text ? { type: '', text: '' } : prev);
      }, 7000);
    }
  };

  const selectedPlan = PRICING_PLANS.find(p => p.id === selectedPlanId) || PRICING_PLANS[0];

  // Derive installment amounts from the initial 70% payment record or fall back to current PRICING_PLANS
  const initialPayRecord = payments.find(p => (p.status === 'successful' || p.status === 'approved') && p.plan === 'premium' && p.notes?.includes('Initial 70%'));
  const finalPayRecord = payments.find(p => (p.status === 'successful' || p.status === 'approved') && p.plan === 'premium' && p.notes?.includes('Final 30%'));
  const pendingFinalPayRecord = payments.find(p => p.status === 'pending' && p.plan === 'premium' && p.notes?.includes('Final 30%'));
  const installmentComplete = Boolean(finalPayRecord);
  const premiumPlanPrice = PRICING_PLANS.find(p => p.id === 'premium')?.price || 200000;
  const paidAmountDue = initialPayRecord?.amount || (premiumPlanPrice * 0.7);
  const remainingAmountDue = initialPayRecord?.amount ? (initialPayRecord.amount / 7) * 3 : (premiumPlanPrice * 0.3);

  // Calculate base amount in NGN
  const getAmountNgn = () => {
    if (checkoutType === 'remaining') return remainingAmountDue;
    if (checkoutType === 'placement') return parseFloat(customAmount) || 0;
    return paymentType === 'installment' && selectedPlan.id === 'premium' 
      ? selectedPlan.price * 0.7 
      : selectedPlan.price;
  };

  const baseAmountNgn = getAmountNgn();

  // Convert amount if currency is USD
  const flutterwaveAmount = currency === 'USD'
    ? Math.round(baseAmountNgn * EXCHANGE_RATE)
    : baseAmountNgn;

  const getPaymentDesc = () => {
    if (checkoutType === 'remaining') return 'Premium Plan - Final 30% Installment';
    if (checkoutType === 'placement') return 'Placement Fee';
    return `${selectedPlan.name} Plan - ${paymentType === 'installment' ? '70% Installment' : 'Full Payment'}`;
  };

  const paymentDesc = getPaymentDesc();

  // Flutterwave config — must be at the top level (React hook rules)
  const flutterwaveConfig = {
    public_key: import.meta.env.VITE_FLUTTERWAVE_PUBLIC_KEY || '',
    tx_ref: `pay_${Date.now()}`,
    amount: flutterwaveAmount,
    currency: currency,
    payment_options: 'card,mobilemoney,ussd',
    customer: {
      email: user?.email || '',
      name: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Customer',
    },
    customizations: {
      title: 'Placement Point',
      description: paymentDesc,
      logo: 'https://st2.depositphotos.com/4403291/7418/v/450/depositphotos_74189661-stock-illustration-online-shop-log.jpg',
    },
  };

  const handleFlutterPayment = useFlutterwave(flutterwaveConfig);

  const handlePaymentSuccess = async (response) => {
    try {
      setLoading(true);
      
      const isRemaining = checkoutType === 'remaining';
      const isPlacement = checkoutType === 'placement';
      const pushPromises = [];

      const newPayment = {
        user_id: user.id,
        amount: baseAmountNgn, // Store NGN amount in DB
        plan: isRemaining ? 'premium' : (isPlacement ? 'placement_fee' : selectedPlanId),
        type: 'flutterwave',
        status: 'successful',
        notes: isRemaining 
          ? 'Final 30% Installment Paid via Flutterwave' 
          : (isPlacement ? 'Placement Fee Paid via Flutterwave' : `${paymentType === 'installment' ? 'Initial 70% Installment' : 'Full Payment'} via Flutterwave`)
      };

      // 1. Insert payment record
      const { error: payError } = await supabase
        .from('payments')
        .insert(newPayment);

      if (payError) throw payError;

      // 2. If it's a new subscription, activate the profile
      if (!isRemaining && !isPlacement) {
        let baseDate = new Date();
        if (profile?.subscription_expiry) {
          const currentExpiry = new Date(profile.subscription_expiry);
          if (currentExpiry > baseDate) {
            baseDate = currentExpiry;
          }
        }
        
        const newExpiry = new Date(baseDate);
        newExpiry.setMonth(newExpiry.getMonth() + 1);

        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            subscription_status: 'active',
            subscription_plan: selectedPlanId,
            subscription_payment_type: isRemaining ? (profile?.subscription_payment_type || 'installment') : (paymentType || 'full'),
            subscription_expiry: newExpiry.toISOString(),
            subscription_renewal_date: newExpiry.toISOString(),
            expiry_warning_sent: false,
            expiry_email_sent: false
          })
          .eq('id', user.id);

        if (profileError) throw profileError;

        // Create notification message
        await supabase.from('messages').insert({
          user_id: user.id,
          message: `✅ Payment Confirmed!\n\nYour online payment of ${convertPrice(baseAmountNgn, currency)} has been successfully verified via Flutterwave, and your ${selectedPlan.name} plan is now active! 🚀\n\nPlease upload your CV, and we will get started immediately. Welcome!`,
          is_from_admin: true,
          admin_has_replied: true,
        });

        // Trigger welcome/confirmation email
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              user_id: user.id,
              subject: 'Subscription Activated! 🎉',
              html: `
                <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
                  <h2 style="color: #4F46E5;">Welcome to Placement Point!</h2>
                  <p>Your online payment has been verified via Flutterwave.</p>
                  <p>Your account is now upgraded to the <strong>${selectedPlan.name}</strong> plan.</p>
                  <p>Please log in to your dashboard to complete your profile and upload your CV so we can start your applications.</p>
                  <br/>
                  <p>Best regards,<br/>The Placement Point Team</p>
                </div>
              `
            }
          });
        } catch (emailErr) {
          console.error('Failed to send confirmation email:', emailErr);
        }

        // Send push notifications
        const senderName = profile ? `${profile.first_name} ${profile.last_name}` : (user.email || 'A client');
        pushPromises.push(
          supabase.functions.invoke('send-push', {
            body: {
              user_id: user.id,
              title: 'Subscription Activated! 🎉',
              body: `Welcome to Placement Point! Your ${selectedPlan.name} plan is now active.`,
              url: '/dashboard/billing'
            }
          }).then(res => {
            if (res.error) throw res.error;
            return res;
          }).catch(e => {
            console.error('Push delivery failed:', e);
            throw e;
          })
        );

        pushPromises.push(
          supabase.functions.invoke('send-push', {
            body: {
              target_role: 'admin',
              title: 'New Subscription! 💰',
              body: `${senderName} has subscribed to the ${selectedPlan.name} plan.`,
              url: '/admin/payments'
            }
          }).then(res => {
            if (res.error) throw res.error;
            return res;
          }).catch(e => {
            console.error('Push delivery failed:', e);
            throw e;
          })
        );

      } else if (isRemaining) {
        // Remaining payment confirmation message
        // Use the persisted remaining amount (derived from the initial 70% payment, not current plan price)
        const premiumRemaining = remainingAmountDue;
        try {
          await supabase.from('messages').insert({
            user_id: user.id,
            message: `✅ Installment Completed!\n\nYour final 30% installment of ${convertPrice(premiumRemaining, currency)} has been successfully received. Your premium plan is now fully paid! Thank you for choosing us! 🚀`,
            is_from_admin: true,
            admin_has_replied: true,
          });
        } catch (msgErr) {
          console.warn('Failed to insert message for remaining payment:', msgErr);
        }

        // Send push notifications
        const senderName = profile ? `${profile.first_name} ${profile.last_name}` : (user.email || 'A client');
        pushPromises.push(
          supabase.functions.invoke('send-push', {
            body: {
              user_id: user.id,
              title: 'Installment Completed! ✅',
              body: `Your final 30% installment of ${convertPrice(premiumRemaining, currency)} has been successfully received.`,
              url: '/dashboard/billing'
            }
          }).then(res => {
            if (res.error) throw res.error;
            return res;
          }).catch(e => {
            console.error('Push delivery failed:', e);
            throw e;
          })
        );

        pushPromises.push(
          supabase.functions.invoke('send-push', {
            body: {
              target_role: 'admin',
              title: 'Installment Completed! 💰',
              body: `${senderName} has paid their remaining 30% installment.`,
              url: '/admin/payments'
            }
          }).then(res => {
            if (res.error) throw res.error;
            return res;
          }).catch(e => {
            console.error('Push delivery failed:', e);
            throw e;
          })
        );

      } else if (isPlacement) {
        // Placement fee confirmation message
        try {
          await supabase.from('messages').insert({
            user_id: user.id,
            message: `✅ Placement Fee Received!\n\nYour placement fee payment of ${convertPrice(baseAmountNgn, currency)} has been successfully received via Flutterwave. Thank you for completing your placement process! 🚀`,
            is_from_admin: true,
            admin_has_replied: true,
          });
        } catch (msgErr) {
          console.warn('Failed to insert message for placement fee:', msgErr);
        }

        // Send push notifications
        const senderName = profile ? `${profile.first_name} ${profile.last_name}` : (user.email || 'A client');
        pushPromises.push(
          supabase.functions.invoke('send-push', {
            body: {
              user_id: user.id,
              title: 'Placement Fee Received! ✅',
              body: `Your placement fee payment of ${convertPrice(baseAmountNgn, currency)} has been successfully received via Flutterwave.`,
              url: '/dashboard/billing'
            }
          }).then(res => {
            if (res.error) throw res.error;
            return res;
          }).catch(e => {
            console.error('Push delivery failed:', e);
            throw e;
          })
        );

        pushPromises.push(
          supabase.functions.invoke('send-push', {
            body: {
              target_role: 'admin',
              title: 'Placement Fee Paid! 💰',
              body: `${senderName} has paid their placement fee.`,
              url: '/admin/payments'
            }
          }).then(res => {
            if (res.error) throw res.error;
            return res;
          }).catch(e => {
            console.error('Push delivery failed:', e);
            throw e;
          })
        );
      }

      // Wait for all push notifications to settle (with a bounded 8 seconds timeout)
      try {
        await Promise.race([
          Promise.allSettled(pushPromises),
          new Promise((resolve) => setTimeout(resolve, 8000))
        ]);
      } catch (err) {
        console.warn('Timeout or error waiting for push notifications to settle:', err);
      }

      showNotification('success', 'Payment successful! Your transaction has been recorded. Refreshing your profile...', false);
      
      // Refresh payments
      const { data } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      setPayments(data || []);
      
      // Reload page to refresh profile context after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (err) {
      console.error('Error recording successful payment:', err);
      showNotification('error', `Payment succeeded but we failed to record it in your profile: ${err.message || 'Please contact support with transaction ID.'}`, false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (triggerPayment) {
      setTriggerPayment(false);
      handleFlutterPayment({
        callback: (response) => {
          closePaymentModal();
          if (response.status === 'successful') {
            handlePaymentSuccess(response);
          } else {
            showNotification('error', 'Payment failed or cancelled. Please try again.');
          }
        },
        onClose: () => {},
      });
    }
  }, [triggerPayment]);

  useEffect(() => {
    if (!user) return;

    const fetchPayments = async () => {
      try {
        const { data, error } = await supabase
          .from('payments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });
        
        // Don't throw if table doesn't exist yet, just catch it
        if (error && error.code !== '42P01') throw error;
        setPayments(data || []);
      } catch (err) {
        console.error('Error fetching payments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [user]);

  const handlePayRemaining = () => {
    setCheckoutType('remaining');
    setShowRemainingModal(true);
  };

  const handleCloseRemainingModal = () => {
    setShowRemainingModal(false);
    setCheckoutType('subscription');
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Billing & Subscription</h1>
        <p className="mt-1 text-slate-500">Manage your active plan, installments, and payment history.</p>
      </div>

      {notification.text && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-3 transition-all duration-300 ${
            notification.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : notification.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-indigo-50 border-indigo-200 text-indigo-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : notification.type === 'error' ? (
            <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 text-sm font-medium pr-8 leading-relaxed">{notification.text}</div>
          <button
            onClick={() => setNotification({ type: '', text: '' })}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0 focus:outline-none transition-colors"
          >
            <span className="text-xl font-bold leading-none">&times;</span>
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Current Plan Card */}
        <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Current Plan</h3>
              <p className="text-sm text-slate-500">Your subscription details</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${
              planStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {planStatus}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-slate-50 rounded-2xl border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-lg capitalize">{planName.replace(/_/g, ' ')} Plan</p>
                {planName === 'premium' ? (
                  <p className="text-sm text-slate-500">15 Applications/week + Strategy Sessions</p>
                ) : planName !== 'Free' ? (
                  <p className="text-sm text-slate-500">Active processing phase.</p>
                ) : (
                  <p className="text-sm text-slate-500">No active applications being sent.</p>
                )}
                {planName !== 'Free' && (
                  <p className="text-xs text-indigo-600 font-semibold mt-1.5 bg-indigo-50/80 px-2 py-0.5 rounded border border-indigo-100/50 inline-block">
                    {profile?.subscription_expiry ? (
                      `Expires: ${new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                    ) : (
                      'Expires: Verification Pending (Confirming transaction)'
                    )}
                  </p>
                )}
              </div>
            </div>
            {planStatus === 'active' && (
              <button
                onClick={() => setShowRenewalSection(!showRenewalSection)}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-indigo-600/10 hover:shadow-indigo-600/20 whitespace-nowrap self-start sm:self-center"
              >
                {showRenewalSection ? 'Hide Renewal Options' : 'Renew / Change Plan'}
              </button>
            )}
          </div>
        </div>

        {/* Installment Tracker (Only show if Premium and on installment plan) */}
        {planName === 'premium' && profile?.subscription_payment_type === 'installment' && !installmentComplete && (
          <div className="bg-gradient-to-br from-slate-900 to-indigo-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden shadow-lg shadow-indigo-900/20">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-400" />
                <h3 className="font-bold">Installment Plan</h3>
              </div>
              <p className="text-indigo-200 text-sm mb-2">You are on the 70/30 installment plan.</p>
              {profile?.subscription_expiry && (
                <p className="text-indigo-300 text-xs mb-6 font-medium">Expires: {new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              )}

              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-indigo-100">Paid (70%)</span>
                  <span className="font-semibold">{convertPrice(paidAmountDue, currency)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-300 font-medium">Remaining (30%)</span>
                  <span className="font-bold text-amber-300">{convertPrice(remainingAmountDue, currency)}</span>
                </div>
                {/* Progress Bar */}
                <div className="w-full bg-slate-800 rounded-full h-2.5 mt-2 overflow-hidden">
                  <div className="bg-green-400 h-2.5 rounded-full" style={{ width: '70%' }}></div>
                </div>
              </div>

              {pendingFinalPayRecord ? (
                <div className="w-full py-3 bg-amber-500/20 text-amber-200 border border-amber-400/30 rounded-xl font-bold text-center text-sm flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4 text-amber-300 animate-spin" />
                  Final Installment Pending Verification
                </div>
              ) : (
                <button 
                  onClick={handlePayRemaining}
                  className="w-full py-3 bg-white text-indigo-900 rounded-xl font-bold hover:bg-slate-100 transition-colors text-sm"
                >
                  Pay Remaining Balance
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {planName !== 'Free' && profile?.subscription_expiry && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <Clock className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 mb-1">Subscription Expires</h3>
              <p className="text-sm text-slate-600 mb-2">
                Your {planName.replace(/_/g, ' ')} plan expires on <strong>{new Date(profile.subscription_expiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
              </p>
              <p className="text-xs text-slate-500">Renew before the expiry date to continue your job applications without interruption.</p>
            </div>
          </div>
        </div>
      )}

      {(planStatus !== 'active' || showRenewalSection) && (
        <div className="space-y-8">
          {/* Plan Selector Title */}
          <div>
            <h2 className="text-xl font-bold text-slate-900">1. Select Your Career Plan</h2>
            <p className="text-sm text-slate-500 mt-1">Pick a plan to accelerate your job search. Select and pay below.</p>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRICING_PLANS.map((plan) => {
              const isSelected = selectedPlanId === plan.id;
              return (
                <div
                  key={plan.id}
                  onClick={() => {
                    setSelectedPlanId(plan.id);
                    if (plan.id !== 'premium') setPaymentType('full');
                  }}
                  className={`bg-white rounded-2xl p-6 border-2 cursor-pointer transition-all relative flex flex-col justify-between ${
                    isSelected
                      ? 'border-indigo-600 shadow-md ring-1 ring-indigo-600/10'
                      : 'border-slate-100 hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 right-4 bg-indigo-600 text-white text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">
                      Most Popular
                    </span>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-extrabold text-slate-900 text-lg capitalize">{plan.name} Plan</h3>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                        isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 text-transparent'
                      }`}>
                        <Check className="w-3.5 h-3.5 font-bold" />
                      </div>
                    </div>

                    <div className="mb-4">
                      <span className="text-3xl font-extrabold text-slate-900">{convertPrice(plan.price, currency)}</span>
                      <span className="text-slate-400 text-xs block mt-1">{plan.paymentNote}</span>
                    </div>

                    <ul className="space-y-2.5 text-xs text-slate-600 border-t border-slate-100 pt-4 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={`w-full py-2.5 rounded-xl text-center text-xs font-bold transition-all ${
                    isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}>
                    {isSelected ? 'Plan Selected' : 'Select Plan'}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Checkout Area */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
            <div className="border-b border-slate-100 pb-6 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">2. Review Checkout Details</h3>
                <p className="text-xs text-slate-400 mt-1">Configure your currency and payment options</p>
              </div>

              {/* Settings selectors */}
              <div className="flex flex-wrap items-center gap-4">
                {/* Currency Switcher */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500">Currency:</span>
                  <div className="flex bg-slate-100 p-1 rounded-xl">
                    <button
                      onClick={() => setCurrency('NGN')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        currency === 'NGN' ? 'bg-white text-indigo-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      NGN (₦)
                    </button>
                    <button
                      onClick={() => setCurrency('USD')}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        currency === 'USD' ? 'bg-white text-indigo-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      USD ($)
                    </button>
                  </div>
                </div>

                {/* Installment Switcher (only for premium) */}
                {selectedPlan.id === 'premium' && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Structure:</span>
                    <div className="flex bg-slate-100 p-1 rounded-xl">
                      <button
                        onClick={() => setPaymentType('full')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          paymentType === 'full' ? 'bg-white text-indigo-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Full
                      </button>
                      <button
                        onClick={() => setPaymentType('installment')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                          paymentType === 'installment' ? 'bg-white text-indigo-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        70/30 Split
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main checkout wrapper */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Left Side: Summary */}
              <div className="lg:col-span-2 space-y-4 lg:border-r lg:border-slate-100 lg:pr-8">
                <h4 className="text-xs uppercase tracking-wider text-slate-400 font-bold">Summary</h4>
                
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 capitalize">{selectedPlan.name} Plan</span>
                    <span className="font-semibold text-slate-800">{convertPrice(selectedPlan.price, currency)}</span>
                  </div>

                  {paymentType === 'installment' && selectedPlan.id === 'premium' && (
                    <>
                      <div className="flex justify-between text-xs text-indigo-600 border-t border-slate-200/50 pt-2">
                        <span>Initial Payment (70%)</span>
                        <span className="font-bold">{convertPrice(selectedPlan.price * 0.7, currency)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>Installment Due (30%)</span>
                        <span>{convertPrice(selectedPlan.price * 0.3, currency)}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between text-base font-extrabold text-slate-900 border-t border-slate-200 pt-3">
                    <span>Total Due Now</span>
                    <span>{convertPrice(baseAmountNgn, currency)}</span>
                  </div>
                </div>

                <div className="flex gap-2 items-start text-[11px] text-slate-400 leading-normal p-2">
                  <Shield className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span>Payments are processed securely. Subscriptions are activated immediately for online payments and manually after receipt validation for bank transfers.</span>
                </div>
              </div>

              {/* Right Side: Options & Forms */}
              <div className="lg:col-span-3">
                {/* Tabs */}
                <div className="flex border-b border-slate-100 mb-6">
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${
                      paymentMethod === 'card'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <CreditCard className="w-4 h-4" /> Pay Online (Instant)
                  </button>
                  <button
                    onClick={() => setPaymentMethod('transfer')}
                    className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${
                      paymentMethod === 'transfer'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <Banknote className="w-4 h-4" /> Bank Transfer
                  </button>
                </div>

                {/* Tab Content 1: Online */}
                {paymentMethod === 'card' && (
                  <div className="space-y-6">
                    <div className="border border-indigo-100 bg-indigo-50/50 rounded-2xl p-4 flex gap-3 text-xs text-indigo-800 leading-relaxed">
                      <Info className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                      <div>
                        <p className="font-bold">Instant Activation</p>
                        <p className="mt-0.5 text-indigo-700/80">Pay online with Flutterwave for instant setup. We accept Cards, Mobile Money, and USSD. No receipt upload required.</p>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        ensurePhoneAndProceed(() => {
                          setCheckoutType('subscription');
                          setTriggerPayment(true);
                        });
                      }}
                      className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-600/10 flex items-center justify-center gap-2 text-sm"
                    >
                      <CreditCard className="w-4 h-4" />
                      Pay {convertPrice(baseAmountNgn, currency)} Now
                    </button>
                  </div>
                )}

                {/* Tab Content 2: Bank Transfer */}
                {paymentMethod === 'transfer' && (
                  <div className="space-y-6">
                    <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-4 text-xs space-y-2">
                      <p className="font-bold text-indigo-900 text-sm">Payment Bank Details:</p>
                      <div className="grid grid-cols-2 gap-y-1 text-indigo-800">
                        <span className="text-slate-400">Bank:</span>
                        <strong className="text-indigo-950">GTBank</strong>
                        <span className="text-slate-400">Account Name:</span>
                        <strong className="text-indigo-950">NELSON EMEM FEYIKEMI</strong>
                        <span className="text-slate-400">Account Number:</span>
                        <strong className="text-indigo-950">0678121228</strong>
                      </div>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const submitAction = async () => {
                          const formData = new FormData(e.target);
                          const file = formData.get('receipt');

                          if (!file?.size) {
                            showNotification('error', 'Please upload your payment receipt.');
                            return;
                          }

                          setIsUploading(true);
                          const finalAmountToRecord = baseAmountNgn;

                          try {
                            // Upload receipt
                            const fileName = `${user.id}/${Date.now()}_${file.name}`;
                            const { data: uploadData, error: uploadError } = await supabase.storage
                              .from('documents')
                              .upload(fileName, file);

                            if (uploadError) throw uploadError;

                            // Insert record
                            const { error: payError } = await supabase
                              .from('payments')
                              .insert({
                                user_id: user.id,
                                amount: finalAmountToRecord,
                                plan: selectedPlanId,
                                type: 'bank_transfer',
                                status: 'pending',
                                receipt_url: uploadData.path,
                                notes: paymentType === 'installment' ? 'Initial 70% Installment Transfer' : 'Full Plan Bank Transfer'
                              });

                            if (payError) throw payError;

                            showNotification('success', 'Transfer receipt uploaded! Our administrators will verify your payment and activate your plan shortly.', false);
                            e.target.reset();

                            // Refresh list
                            const { data } = await supabase
                              .from('payments')
                              .select('*')
                              .eq('user_id', user.id)
                              .order('created_at', { ascending: false });
                            setPayments(data || []);
                          } catch (err) {
                            console.error('Error submitting bank receipt:', err);
                            showNotification('error', `Failed to submit bank receipt: ${err.message || 'Please try again.'}`, false);
                          } finally {
                            setIsUploading(false);
                          }
                        };
                        ensurePhoneAndProceed(submitAction);
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Upload Receipt Screenshot (PNG, JPG, PDF) *</label>
                        <input
                          type="file"
                          name="receipt"
                          accept="image/*,.pdf"
                          required
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-extrabold file:bg-indigo-50 file:text-indigo-700"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isUploading}
                        className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 text-sm shadow-md"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                            Uploading Receipt...
                          </>
                        ) : (
                          <>
                            <Banknote className="w-4 h-4" />
                            Submit Receipt For Verification
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Placement Fee Payment */}
      {planStatus === 'active' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Pay Placement Fee</h3>
          <p className="text-sm text-slate-500 mb-6">Congratulations on landing the job! You can pay your placement fee online or upload a transfer receipt.</p>

          {/* Tabs */}
          <div className="flex border-b border-slate-100 mb-6">
            <button
              onClick={() => setPlacementMethod('card')}
              className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${
                placementMethod === 'card'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <CreditCard className="w-4 h-4" /> Pay Online (Instant)
            </button>
            <button
              onClick={() => setPlacementMethod('transfer')}
              className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${
                placementMethod === 'transfer'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`}
            >
              <Banknote className="w-4 h-4" /> Bank Transfer
            </button>
          </div>

          {placementMethod === 'card' ? (
            <div className="space-y-6">
              <div className="border border-indigo-100 bg-indigo-50/50 rounded-xl p-4 flex gap-3 text-xs text-indigo-800 leading-relaxed">
                <Info className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="font-bold">Instant Online Verification</p>
                  <p className="mt-0.5 text-indigo-700/80">Pay online with Flutterwave using Card, USSD, or Mobile Money. Perfect for international card payments.</p>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const proceed = () => {
                  const formData = new FormData(e.target);
                  const amt = parseFloat(formData.get('amount'));
                  
                  if (!amt || isNaN(amt) || amt <= 0) {
                    showNotification('error', 'Please enter a valid payment amount.');
                    return;
                  }

                  setCheckoutType('placement');
                  setCustomAmount(amt.toString());
                  setTriggerPayment(true);
                };
                ensurePhoneAndProceed(proceed);
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount to Pay ({currency === 'USD' ? '$' : '₦'}) *</label>
                  <input
                    type="number"
                    name="amount"
                    min="100"
                    required
                    placeholder={currency === 'USD' ? 'e.g. 200' : 'e.g. 200000'}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-600/10 flex items-center justify-center gap-2 text-sm"
                >
                  <CreditCard className="w-4 h-4" />
                  Pay Placement Fee Online
                </button>
              </form>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm">
                <p className="font-bold text-indigo-900 mb-2">Bank Details:</p>
                <p className="text-indigo-800">Bank: <strong>GTBank</strong></p>
                <p className="text-indigo-800">Account Name: <strong>NELSON EMEM FEYIKEMI</strong></p>
                <p className="text-indigo-800">Account Number: <strong>0678121228</strong></p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const submitAction = async () => {
                  const formData = new FormData(e.target);
                  const file = formData.get('receipt');
                  const amount = parseFloat(formData.get('amount'));
                  
                  if (!file?.size || !amount || isNaN(amount)) {
                    showNotification('error', 'Please enter a valid amount and upload your receipt.');
                    return;
                  }

                  setIsUploading(true);
                  try {
                    // Upload receipt to storage
                    const fileName = `${user.id}/${Date.now()}_placement_${file.name}`;
                    const { data: uploadData, error: uploadError } = await supabase.storage
                      .from('documents')
                      .upload(fileName, file);

                    if (uploadError) throw uploadError;

                    // Create payment record
                    const { error: payError } = await supabase
                      .from('payments')
                      .insert({
                        user_id: user.id,
                        amount: amount,
                        plan: 'placement_fee',
                        type: 'bank_transfer',
                        status: 'pending',
                        receipt_url: uploadData.path,
                        notes: 'Placement Fee Bank Transfer'
                      });

                    if (payError) throw payError;

                    showNotification('success', 'Receipt uploaded! Thank you for completing your placement payment.', false);
                    e.target.reset();
                    
                    // Refresh payments list
                    const { data } = await supabase
                      .from('payments')
                      .select('*')
                      .eq('user_id', user.id)
                      .order('created_at', { ascending: false });
                    setPayments(data || []);
                  } catch (err) {
                    console.error('Error uploading receipt:', err);
                    showNotification('error', `Failed to upload receipt: ${err.message || 'Please try again.'}`, false);
                  } finally {
                    setIsUploading(false);
                  }
                };
                ensurePhoneAndProceed(submitAction);
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount Paid (₦) *</label>
                  <input
                    type="number"
                    name="amount"
                    min="1000"
                    required
                    placeholder="e.g. 200000"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload Transfer Receipt *</label>
                  <input
                    type="file"
                    name="receipt"
                    accept="image/*,.pdf"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-indigo-50 file:text-indigo-700 bg-white"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="w-full py-3.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-colors flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-white" />
                      Uploading Receipt...
                    </>
                  ) : (
                    'Submit Placement Fee Receipt'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Payment History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
        <h3 className="text-lg font-bold text-slate-900 mb-6">Payment History</h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : payments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 text-sm text-slate-500 font-medium">
                  <th className="pb-3 pr-4">Date</th>
                  <th className="pb-3 px-4">Amount</th>
                  <th className="pb-3 px-4">Plan</th>
                  <th className="pb-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-50">
                    <td className="py-4 pr-4 text-slate-600 whitespace-nowrap">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4 font-semibold text-slate-900 whitespace-nowrap">
                      {convertPrice(payment.amount, currency)}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap capitalize">
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-indigo-100 text-indigo-700">
                        {payment.plan || payment.type?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap">
                      <div>
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          (payment.status === 'successful' || payment.status === 'approved') ? 'bg-green-100 text-green-700' :
                          payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {(payment.status === 'successful' || payment.status === 'approved') ? 'Verified ✓' : payment.status === 'pending' ? 'Pending Review' : 'Failed'}
                        </span>
                        {payment.status === 'failed' && payment.notes && (
                          <p className="text-[11px] text-red-500 mt-1 max-w-xs whitespace-normal italic">
                            Reason: {payment.notes}
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
              <CreditCard className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-600 font-medium">No payments yet</p>
            <p className="text-sm text-slate-400 mt-1 max-w-sm">When you make a payment, your receipts will appear here.</p>
          </div>
        )}
      </div>

      {/* Remaining Installment Payment Modal */}
      {showRemainingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl border border-slate-100 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-lg text-slate-905">Complete Installment Payment</h3>
                </div>
                <button
                  onClick={handleCloseRemainingModal}
                  className="text-slate-400 hover:text-slate-600 focus:outline-none"
                >
                  <span className="text-2xl leading-none">&times;</span>
                </button>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 mb-6 flex justify-between items-center text-sm">
                <span className="text-slate-500 font-medium">Final 30% Installment Due:</span>
                <strong className="text-lg font-extrabold text-indigo-900">{convertPrice(remainingAmountDue, currency)}</strong>
              </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-100 mb-6">
                  <button
                    onClick={() => setRemainingMethod('card')}
                    className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${
                      remainingMethod === 'card'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <CreditCard className="w-4 h-4" /> Pay Online (Instant)
                  </button>
                  <button
                    onClick={() => setRemainingMethod('transfer')}
                    className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${
                      remainingMethod === 'transfer'
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <Banknote className="w-4 h-4" /> Bank Transfer
                  </button>
                </div>

                {remainingMethod === 'card' ? (
                  <div className="space-y-6">
                    <div className="border border-indigo-100 bg-indigo-50/50 rounded-xl p-4 flex gap-3 text-xs text-indigo-800 leading-relaxed">
                      <Info className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                      <div>
                        <p className="font-bold">Instant Activation</p>
                        <p className="mt-0.5 text-indigo-700/80">Pay online with Flutterwave. We accept Cards, Mobile Money, and USSD. The system updates your profile instantly.</p>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        ensurePhoneAndProceed(() => {
                          setCheckoutType('remaining');
                          setTriggerPayment(true);
                          setShowRemainingModal(false);
                        });
                      }}
                      className="w-full py-3.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-600/10 flex items-center justify-center gap-2 text-sm"
                    >
                      <CreditCard className="w-4 h-4" />
                      Pay {convertPrice(remainingAmountDue, currency)} Now
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-4 text-xs space-y-2">
                      <p className="font-bold text-indigo-900 text-sm">Payment Bank Details:</p>
                      <div className="grid grid-cols-2 gap-y-1 text-indigo-800">
                        <span className="text-slate-400">Bank:</span>
                        <strong className="text-indigo-950">GTBank</strong>
                        <span className="text-slate-400">Account Name:</span>
                        <strong className="text-indigo-950">NELSON EMEM FEYIKEMI</strong>
                        <span className="text-slate-400">Account Number:</span>
                        <strong className="text-indigo-950">0678121228</strong>
                      </div>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const submitAction = async () => {
                          const formData = new FormData(e.target);
                          const file = formData.get('receipt');

                          if (!file?.size) {
                            showNotification('error', 'Please upload your payment receipt.');
                            return;
                          }

                          setIsUploading(true);
                          try {
                            const fileName = `${user.id}/${Date.now()}_remaining_${file.name}`;
                            const { data: uploadData, error: uploadError } = await supabase.storage
                              .from('documents')
                              .upload(fileName, file);

                            if (uploadError) throw uploadError;

                            const { error: dbError } = await supabase
                              .from('payments')
                              .insert({
                                user_id: user.id,
                                amount: remainingAmountDue,
                                plan: 'premium',
                                type: 'bank_transfer',
                                status: 'pending',
                                receipt_url: uploadData.path,
                                notes: 'Final 30% Installment Transfer'
                              });

                          if (dbError) throw dbError;

                          showNotification('success', 'Installment receipt uploaded! Our administrators will verify your payment and record it shortly.', false);
                          handleCloseRemainingModal();

                          // Refresh list
                          const { data } = await supabase
                            .from('payments')
                            .select('*')
                            .eq('user_id', user.id)
                            .order('created_at', { ascending: false });
                          setPayments(data || []);
                        } catch (err) {
                          console.error('Error submitting installment receipt:', err);
                          showNotification('error', `Failed to submit installment receipt: ${err.message || 'Please try again.'}`, false);
                        } finally {
                          setIsUploading(false);
                        }
                      };
                      ensurePhoneAndProceed(submitAction);
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Upload Receipt Screenshot (PNG, JPG, PDF) *</label>
                      <input
                        type="file"
                        name="receipt"
                        accept="image/*,.pdf"
                        required
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-extrabold file:bg-indigo-50 file:text-indigo-700 bg-white"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isUploading}
                      className="w-full py-3.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 text-sm shadow-md"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin text-white" />
                          Uploading Receipt...
                        </>
                      ) : (
                        <>
                          <Banknote className="w-4 h-4" />
                          Submit Receipt For Verification
                        </>
                      )}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Number Requirement Modal */}
      {phoneRequiredModal.isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-100 space-y-4">
            <div className="flex items-center gap-3 text-indigo-600">
              <div className="p-3 bg-indigo-50 rounded-xl">
                <Phone className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Phone Number Required</h3>
                <p className="text-xs text-slate-500">Required to process your payment securely</p>
              </div>
            </div>
            
            <p className="text-sm text-slate-600 leading-relaxed">
              We couldn't find a phone number associated with your profile. Please provide a valid phone number below to complete your checkout and activate support.
            </p>

            <form onSubmit={handleSavePhone} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Phone Number (Supports International Format)
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. +234 803 123 4567 or +1 555 123 4567"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    setPhoneError('');
                  }}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50 font-medium"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Format: Enter digits with country code (e.g. +44 7911 123456 or +2347086371665).
                </p>
                {phoneError && (
                  <p className="text-xs text-rose-600 font-semibold mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {phoneError}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setPhoneRequiredModal({ isOpen: false, callback: null })}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel Payment
                </button>
                <button
                  type="submit"
                  disabled={savingPhone}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-indigo-100 flex items-center gap-1.5 disabled:opacity-50"
                >
                  {savingPhone ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

